import React, { useContext, useEffect, useState } from "react";
import { LoginBox } from "./LoginBox";
import classNames from "classnames";
import { SimpleFormView } from "../core/views/UForm";
import { Spin } from "../core/views/Spin";
import { googleAuthenticationPayload, OrSeparator } from "./SocialLogin";
import { RouteComponentProps, withRouter } from "react-router";
import { BaseFormWithArrayFields } from "../core/BaseForm";
import { GlobalContext } from "../GlobalContext";
import Box from "@mui/material/Box";
import { Button } from "../core/views/Button";
import { UFormField } from "../core/views/UFormField";
import { FormFields } from "../core/api/contracts/FormFields";
import { AzureSso } from "./login/azure";
import { authenticationResultToObject } from "./login/azure/AzureAuthenticationObject";
import { AuthenticationResult } from "@azure/msal-browser";
import { useLoginStateProvider } from "../LoginStateProvider";
import GoogleRedirectButton from "./GoogleRedirectButton";

const formConfig = new FormFields()
  .addField("name", "required|string", "Name", "static", null, null, null, true)
  .addField("email", "required|email", "Email", "static", null, null, null, true)
  .addField("company", "required|string", "Company Name", "static", null, null, null, true)
  .addField("password", "required|min:6", "Password", "password")
  .addField("confirmPassword", "required|same:password", "Repeat Password", "password");

function InvitationNotFound() {
  return (
    <div>
      <p>Invitation not found. Perhaps you've already used this invitation.</p>
      <Box pt={2}>
        <Button to="/" type="primary">
          Continue
        </Button>
      </Box>
    </div>
  );
}

type AcceptInviteViewProps = { googleSupport?: boolean } & RouteComponentProps<{ id: string }>;

function AcceptInviteView(props: AcceptInviteViewProps) {
  const context = useContext(GlobalContext);
  const [form, setForm] = useState<BaseFormWithArrayFields>();
  const [loading, setLoading] = useState(true);
  const [invitationNotFound, setInviteNotFound] = useState(false);
  const [details, setDetails] = useState<any>();
  const loginStateProvider = useLoginStateProvider();

  useEffect(() => {
    const onInvitationNotFound = () => {
      setInviteNotFound(true);
      setLoading(false);
    };
    const formInstance = formConfig.createForm();
    formInstance.$("name").metadata.disabled = true;
    setLoading(true);
    setForm(formInstance);
    context.auth.getInvitationDetails(props.match.params.id, onInvitationNotFound).then((details) => {
      setDetails(details);
    });
  }, [props.match.params.id, context.auth]);

  useEffect(() => {
    if (details && form) {
      form.$("name").sync(details.user.name);
      form.$("email").sync(details.user.email);
      form.$("company").sync(details.organization);
      setLoading(false);
    }
  }, [details, form]);

  const signUp = (values: { password: string }) => {
    context.auth
      .acceptInvitation(props.match.params.id, values.password)
      .then(loginStateProvider.handleLoginReply)
      .then(() => props.history.push("/"));
  };

  const google = (obj: unknown) => {
    context.auth
      .acceptInvitationGoogle(props.match.params.id, googleAuthenticationPayload(obj))
      .then(loginStateProvider.handleLoginReply)
      .then(() => props.history.push("/"));
  };

  const azure = (obj: AuthenticationResult) => {
    return context.auth
      .acceptInvitationAzure(props.match.params.id, authenticationResultToObject(obj))
      .then(loginStateProvider.handleLoginReply)
      .then(() => props.history.push("/"));
  };

  return (
    <LoginBox className={classNames("signup invitation")}>
      <Box minHeight={180}>
        <h1>Accept Invitation</h1>

        <Spin spinning={loading}>
          {!loading &&
            (invitationNotFound ? (
              <InvitationNotFound />
            ) : (
              <div>
                {props.googleSupport && (
                  <>
                    {/*<GoogleLoginButton onSuccess={google} />*/}
                    <GoogleRedirectButton invitationId={props.match.params.id} />
                    <AzureSso
                      onAuthenticated={azure}
                      redirectUri={window.location.href.slice(0, window.location.href.lastIndexOf("/"))}
                    />
                    <OrSeparator />
                  </>
                )}
                <div className="create-feature-form">
                  <SimpleFormView form={form} onSuccess={signUp} disabled={loading}>
                    <div className="form-input-group">
                      <UFormField input={form.$("name")} />
                      <UFormField input={form.$("company")} />
                    </div>

                    <div className="form-input-group">
                      <UFormField input={form.$("email")} />
                    </div>

                    <div className="form-input-group">
                      <UFormField input={form.$("password")} />
                      <UFormField input={form.$("confirmPassword")} />
                    </div>

                    <Box width="100%" textAlign="center" mt={3}>
                      <Button type="submit">Sign Up</Button>
                    </Box>
                  </SimpleFormView>
                </div>
              </div>
            ))}
        </Spin>
      </Box>
    </LoginBox>
  );
}

(AcceptInviteView as React.FunctionComponent<AcceptInviteViewProps>).defaultProps = {
  googleSupport: true,
};

export default withRouter(AcceptInviteView);
